import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { fmtCupo } from '../utils/format';
import { toast } from 'sonner';
import { Grid3X3, LogOut, Calendar, Users, Search, X, ChevronLeft, ChevronRight, Clock, MapPin, UserPlus, Check, GraduationCap, UserCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DAYS = ['Lun','Mar','Mie','Jue','Vie','Sab','Dom'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const toTitle = (s) => s ? s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase()) : '';
const fmtRut = (r) => { if (!r) return ''; const c = r.replace(/[.\-]/g, ''); return c.slice(0,-1).replace(/\B(?=(\d{3})+(?!\d))/g,'.') + '-' + c.slice(-1); };
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return `${p[2]}-${p[1]}-${p[0]}`; };

export default function CapacitacionViewer() {
  const navigate = useNavigate();
  const { logout, token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [logoImage, setLogoImage] = useState(() => localStorage.getItem('cached_logo_url'));
  const [sesiones, setSesiones] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedSesion, setSelectedSesion] = useState(null);
  const [inscripciones, setInscripciones] = useState([]);
  const [showInscribir, setShowInscribir] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [workerSearch, setWorkerSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateWorker, setShowCreateWorker] = useState(false);
  const [newWorker, setNewWorker] = useState({ rut: '', nombre: '', apellido: '', email: '' });
  const [moreDayModal, setMoreDayModal] = useState(null); // { dateStr, sesiones }

  useEffect(() => {
    if (!logoImage) {
      axios.get(`${API}/configuracion`, { headers }).then(r => {
        if (r.data.logo_image) {
          let url = r.data.logo_image;
          if (!url.startsWith('http') && !url.startsWith('data:')) url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
          setLogoImage(url); localStorage.setItem('cached_logo_url', url);
        }
      }).catch(() => {});
    }
  }, []);

  const fetchSesiones = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sesiones`, { headers });
      // Mostrar todas las sesiones agendadas/abiertas (excluyendo cerradas / canceladas)
      setSesiones((res.data || []).filter(s => s.estado === 'abierta' || s.estado === 'programada'));
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchSesiones(); }, [fetchSesiones]);

  const fetchInscripciones = async (sesionId) => {
    try {
      const res = await axios.get(`${API}/sesiones/${sesionId}/inscripciones`, { headers });
      setInscripciones(res.data);
    } catch {}
  };

  const handleSelectSesion = (s) => {
    setSelectedSesion(s);
    fetchInscripciones(s.id);
  };

  // Calendar logic
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const totalDays = lastDay.getDate();

  const sesionsByDate = {};
  sesiones.forEach(s => { const d = s.fecha; if (!sesionsByDate[d]) sesionsByDate[d] = []; sesionsByDate[d].push(s); });

  const calendarDays = [];
  for (let i = 0; i < startOffset; i++) calendarDays.push(null);
  for (let d = 1; d <= totalDays; d++) calendarDays.push(d);

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  // Inscribir modal
  const openInscribir = async () => {
    setShowInscribir(true);
    setWorkerSearch('');
    setSelectedWorkers([]);
    fetchWorkers('');
  };

  const fetchWorkers = async (search) => {
    try {
      const res = await axios.get(`${API}/personal-mandante?page=1&limit=1200&search=${encodeURIComponent(search)}`, { headers });
      setWorkers(res.data.data || []);
    } catch { setWorkers([]); }
  };

  useEffect(() => {
    if (showInscribir) {
      const t = setTimeout(() => fetchWorkers(workerSearch), 300);
      return () => clearTimeout(t);
    }
  }, [workerSearch, showInscribir]);

  const filteredWorkers = workers;

  const toggleWorker = (w) => {
    setSelectedWorkers(prev => prev.find(s => s.rut === w.rut) ? prev.filter(s => s.rut !== w.rut) : [...prev, w]);
  };

  const handleInscribir = async () => {
    if (!selectedSesion || selectedWorkers.length === 0) return;
    try {
      await axios.post(`${API}/sesiones/inscribir`, {
        sesion_id: selectedSesion.id,
        empresa_id: null,
        trabajadores: selectedWorkers.map(w => ({ rut: w.rut, nombre: w.nombre }))
      }, { headers });
      toast.success(`${selectedWorkers.length} trabajador(es) inscrito(s)`);
      setShowInscribir(false);
      fetchInscripciones(selectedSesion.id);
      fetchSesiones();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al inscribir'); }
  };

  const cancelarInscripcion = async (inscId) => {
    try {
      await axios.delete(`${API}/sesiones/inscripcion/${inscId}`, { headers });
      toast.success('Inscripcion cancelada');
      fetchInscripciones(selectedSesion.id);
      fetchSesiones();
    } catch { toast.error('Error'); }
  };

  const handleCreateWorker = async () => {
    if (!newWorker.rut || !newWorker.nombre || !newWorker.apellido) {
      toast.error('RUT, Nombre y Apellido son obligatorios');
      return;
    }
    try {
      await axios.post(`${API}/personas`, {
        nombre: newWorker.nombre.toUpperCase(),
        apellido: newWorker.apellido.toUpperCase(),
        rut: newWorker.rut.replace(/[.\-]/g, ''),
        email: newWorker.email || null,
        sexo: 'M',
        nacionalidad: 'Chilena'
      }, { headers });
      toast.success(`Trabajador ${newWorker.nombre} ${newWorker.apellido} creado`);
      setNewWorker({ rut: '', nombre: '', apellido: '', email: '' });
      setShowCreateWorker(false);
      fetchWorkers(workerSearch);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear trabajador');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="capacitacion-viewer">
      <header className="px-5 py-3 flex items-center justify-between shrink-0" style={{ backgroundColor: '#1B919B' }}>
        <div className="flex items-center gap-4">
          {logoImage && <img src={logoImage} alt="Logo" className="h-12 object-contain" style={{ filter: 'brightness(0) invert(1)' }}
            onError={() => { setLogoImage(null); localStorage.removeItem('cached_logo_url'); }} />}
          <div>
            <h1 className="text-base font-bold text-white tracking-wide">Capacitación</h1>
            <p className="text-xs text-white/70">Sesiones disponibles · vista de calendario</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/perfil-viewer')} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="volver-perfil-btn">
            <UserCircle className="w-3.5 h-3.5" /> Volver a Mi Perfil
          </button>
          <button onClick={() => navigate('/seleccionar-rol')} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="volver-tysta-btn">
            <Grid3X3 className="w-3.5 h-3.5" /> TystaCore
          </button>
          <button onClick={() => { logout(); navigate('/login'); }} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Salir
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
          {/* Calendar */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                <button onClick={prevMonth} className="p-1 hover:bg-slate-200 rounded"><ChevronLeft className="w-5 h-5 text-slate-600" /></button>
                <h2 className="text-sm font-bold text-slate-800">{MONTHS[month]} {year}</h2>
                <button onClick={nextMonth} className="p-1 hover:bg-slate-200 rounded"><ChevronRight className="w-5 h-5 text-slate-600" /></button>
              </div>
              <div className="grid grid-cols-7">
                {DAYS.map(d => <div key={d} className="text-center text-[10px] font-semibold text-slate-400 uppercase py-2 border-b">{d}</div>)}
                {calendarDays.map((day, i) => {
                  if (!day) return <div key={`e-${i}`} className="min-h-[100px] border-b border-r border-slate-100 bg-slate-50/50" />;
                  const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const daySesiones = sesionsByDate[dateStr] || [];
                  const isToday = new Date().toISOString().slice(0,10) === dateStr;
                  const maxShow = 2;
                  return (
                    <div key={day} className={`min-h-[100px] border-b border-r border-slate-100 p-1 overflow-hidden ${isToday ? 'bg-teal-50/50' : ''}`}>
                      <span className={`text-xs font-medium mb-1 block ${isToday ? 'bg-teal-500 text-white w-5 h-5 rounded-full inline-flex items-center justify-center' : 'text-slate-600'}`}>{day}</span>
                      <div className="space-y-0.5">
                        {daySesiones.slice(0, maxShow).map(s => (
                          <button key={s.id} onClick={() => handleSelectSesion(s)}
                            className={`w-full text-left text-[9px] px-1 py-0.5 rounded truncate font-medium transition-colors ${selectedSesion?.id === s.id ? 'bg-teal-600 text-white' : 'bg-teal-100 text-teal-800 hover:bg-teal-200'}`}>
                            {s.capacitacion_nombre}
                          </button>
                        ))}
                        {daySesiones.length > maxShow && (
                          <button
                            type="button"
                            data-testid={`more-sessions-btn-${dateStr}`}
                            onClick={(e) => { e.stopPropagation(); setMoreDayModal({ dateStr, sesiones: daySesiones }); }}
                            className="w-full text-left text-[9px] text-teal-600 font-semibold px-1 py-0.5 rounded hover:bg-teal-100 hover:text-teal-800 transition-colors cursor-pointer"
                          >
                            +{daySesiones.length - maxShow} mas
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {loading && <div className="flex justify-center py-4"><div className="animate-spin h-5 w-5 border-b-2 border-teal-500 rounded-full" /></div>}
          </div>

          {/* Detalle sesion */}
          <div className="space-y-4">
            {selectedSesion ? (
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center shrink-0">
                    <GraduationCap className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{selectedSesion.capacitacion_nombre}</h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(selectedSesion.fecha)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{selectedSesion.hora_inicio} - {selectedSesion.hora_fin}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                      <MapPin className="w-3 h-3" />{selectedSesion.lugar}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                  <span className="text-xs text-slate-500">Cupos</span>
                  <span className="text-sm font-bold text-slate-800">{selectedSesion.inscritos} / {fmtCupo(selectedSesion.cupo_maximo)}</span>
                </div>

                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                  <span className="text-xs text-emerald-700">Cupos disponibles</span>
                  <span className="text-sm font-bold text-emerald-700">{Math.max(0, (selectedSesion.cupo_maximo || 0) - (selectedSesion.inscritos || 0))}</span>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Selecciona una sesion del calendario</p>
                <p className="text-xs text-slate-400 mt-1">Haz click en una capacitacion para ver dia, lugar y cupos disponibles</p>
              </div>
            )}

            {/* Quick stats */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Sesiones abiertas</p>
              <div className="space-y-1.5">
                {sesiones.slice(0, 5).map(s => (
                  <button key={s.id} onClick={() => handleSelectSesion(s)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors ${selectedSesion?.id === s.id ? 'bg-teal-50 border border-teal-200' : 'hover:bg-slate-50'}`}>
                    <div className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-800 truncate">{s.capacitacion_nombre}</p>
                      <p className="text-[10px] text-slate-400">{fmtDate(s.fecha)} | {s.inscritos}/{fmtCupo(s.cupo_maximo)} cupos</p>
                    </div>
                  </button>
                ))}
                {sesiones.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No hay sesiones abiertas</p>}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal ver todas las sesiones del día */}
      {moreDayModal && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setMoreDayModal(null)} data-testid="more-sessions-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-base font-bold text-slate-900">Sesiones del día</h2>
                <p className="text-xs text-slate-500">{fmtDate(moreDayModal.dateStr)} &middot; {moreDayModal.sesiones.length} sesion(es)</p>
              </div>
              <button onClick={() => setMoreDayModal(null)} className="p-1 hover:bg-slate-100 rounded" data-testid="close-more-sessions-modal"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {moreDayModal.sesiones.map(s => (
                <button
                  key={s.id}
                  data-testid={`day-session-${s.id}`}
                  onClick={() => { handleSelectSesion(s); setMoreDayModal(null); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${selectedSesion?.id === s.id ? 'bg-teal-50 border-teal-300' : 'border-slate-200 hover:bg-slate-50'}`}
                >
                  <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center shrink-0">
                    <GraduationCap className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate">{s.capacitacion_nombre}</p>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-500">
                      {s.hora_inicio && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{s.hora_inicio}{s.hora_fin ? ` - ${s.hora_fin}` : ''}</span>}
                      {s.lugar && <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{s.lugar}</span>}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5">{s.inscritos ?? 0}/{fmtCupo(s.cupo_maximo ?? 0)} cupos</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal inscribir */}
      {showInscribir && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowInscribir(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-base font-bold text-slate-900">Inscribir Personal Mandante</h2>
                <p className="text-xs text-slate-500">{selectedSesion?.capacitacion_nombre} - {fmtDate(selectedSesion?.fecha)}</p>
              </div>
              <button onClick={() => setShowInscribir(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="px-5 py-3 border-b shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input type="text" value={workerSearch} onChange={e => setWorkerSearch(e.target.value)}
                  placeholder="Buscar por nombre o RUT..."
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm" data-testid="worker-search" />
              </div>
              <div className="flex items-center justify-between mt-2">
                {selectedWorkers.length > 0 ? (
                  <div className="flex flex-wrap gap-1 flex-1">
                    {selectedWorkers.map(w => (
                      <span key={w.rut} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-[10px] font-medium">
                        {w.nombre}
                        <button onClick={() => toggleWorker(w)} className="hover:text-teal-900"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                ) : <div />}
                <button onClick={() => setShowCreateWorker(!showCreateWorker)}
                  className="shrink-0 flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium px-2 py-1 rounded hover:bg-teal-50"
                  data-testid="toggle-create-worker">
                  <UserPlus className="w-3.5 h-3.5" /> {showCreateWorker ? 'Cerrar' : 'Crear Trabajador'}
                </button>
              </div>
              {showCreateWorker && (
                <div className="mt-2 p-3 bg-teal-50 rounded-lg border border-teal-200 space-y-2" data-testid="create-worker-form">
                  <p className="text-[10px] font-semibold text-teal-700 uppercase">Nuevo Trabajador</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={newWorker.rut} onChange={e => setNewWorker({ ...newWorker, rut: e.target.value })}
                      placeholder="RUT (ej: 12345678-9)" className="px-2.5 py-1.5 border rounded text-xs" data-testid="new-worker-rut" />
                    <input type="text" value={newWorker.email} onChange={e => setNewWorker({ ...newWorker, email: e.target.value })}
                      placeholder="Email (opcional)" className="px-2.5 py-1.5 border rounded text-xs" />
                    <input type="text" value={newWorker.nombre} onChange={e => setNewWorker({ ...newWorker, nombre: e.target.value })}
                      placeholder="Nombre *" className="px-2.5 py-1.5 border rounded text-xs" data-testid="new-worker-nombre" />
                    <input type="text" value={newWorker.apellido} onChange={e => setNewWorker({ ...newWorker, apellido: e.target.value })}
                      placeholder="Apellido *" className="px-2.5 py-1.5 border rounded text-xs" data-testid="new-worker-apellido" />
                  </div>
                  <button onClick={handleCreateWorker}
                    className="w-full py-1.5 bg-teal-600 text-white text-xs font-medium rounded hover:bg-teal-700"
                    data-testid="confirm-create-worker">
                    Crear y Agregar a Lista
                  </button>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-2">
              {filteredWorkers.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No se encontraron trabajadores</p>
              ) : filteredWorkers.map(w => {
                const isSelected = selectedWorkers.some(s => s.rut === w.rut);
                const isAlreadyInscribed = inscripciones.some(i => i.rut_trabajador === w.rut?.replace(/[.\-]/g, '') || fmtRut(i.rut_trabajador) === w.rut);
                return (
                  <button key={w.id} onClick={() => !isAlreadyInscribed && toggleWorker(w)} disabled={isAlreadyInscribed}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${isAlreadyInscribed ? 'opacity-40 cursor-not-allowed' : isSelected ? 'bg-teal-50 border border-teal-200' : 'hover:bg-slate-50'}`}>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${isSelected ? 'bg-teal-500 border-teal-500' : isAlreadyInscribed ? 'bg-slate-200 border-slate-300' : 'border-slate-300'}`}>
                      {(isSelected || isAlreadyInscribed) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-slate-800">{w.nombre}</p>
                      <p className="text-[10px] text-slate-400 font-mono">{w.rut} {w.email ? `| ${w.email}` : ''}</p>
                    </div>
                    {isAlreadyInscribed && <span className="text-[10px] text-slate-400">Ya inscrito</span>}
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-3 border-t flex items-center justify-between shrink-0">
              <span className="text-xs text-slate-400">{selectedWorkers.length} seleccionado(s)</span>
              <div className="flex gap-2">
                <button onClick={() => setShowInscribir(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
                <button onClick={handleInscribir} disabled={selectedWorkers.length === 0}
                  className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40" data-testid="confirmar-inscripcion-btn">
                  Inscribir ({selectedWorkers.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
      <footer className="py-2 text-center shrink-0"><p className="text-xs text-slate-400">&copy; TystaCore {new Date().getFullYear()}</p></footer>
    </div>
  );
}
